import {
  ChangeEvent,
  FormEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {useNavigate, useUrl} from '@shopify/hydrogen';
import classNames from 'classnames';
import 'rc-slider/assets/index.css';

import {
  filterByPriceOptions,
  filterInStockOptions,
  filterRanges,
  defaultWhiskyTypes,
} from '../constants';
import VectorIcon from './VectorIcon';

import {useShopFilterUrlEncode, useShopifyTagResolver} from '../hooks';
import {FilterRange, getSearchQuery, ShopFilterParamsType} from '../utils';
import {CountryOfOrigin, ProductCategory} from '../types';
import {useGlobalContainer} from '../contexts/GlobalContainerProvider.client';
import ShopFilterSelect from './shop/ShopFilterSelect.client';
import ToggleSwitchField from './formControls/ToggleSwitchField.client';
import ShopFilterSlider from './shop/ShopFilterSlider.client';
import {DynamicShopFilterTag} from '../hooks/useShopifyTagResolver';
import coerceIsTrue from '../utils/coerceIsTrue';

interface ShopFilterProps {
  readonly productTags: DynamicShopFilterTag;
}

export default function ShopFilter({
  productTags,
}: ShopFilterProps): ReactElement {
  const {pathname} = useUrl();
  const {getFilterOptions} = useShopifyTagResolver(productTags);

  const filterByBrandOptions = useMemo(
    () => getFilterOptions('brand'),
    [getFilterOptions],
  );

  const filterWhiskyByStyleOptions = useMemo(
    () => getFilterOptions('style'),
    [getFilterOptions],
  );

  const filterByUserRatingOptions = useMemo(
    () =>
      getFilterOptions('averageRatingGroup').map(({value, label}) => ({
        value,
        label: (
          <span
            className={`reviews__item-content__rating rating-graph rate_${label}`}
          />
        ),
      })),
    [getFilterOptions],
  );

  const filterByWhiskyTypeOptions = useMemo(
    () => getFilterOptions('type'),
    [getFilterOptions],
  );

  const {setIsShopFilterActive} = useGlobalContainer();

  const toggleFilters = useCallback(
    () => setIsShopFilterActive((active) => !active),
    [setIsShopFilterActive],
  );

  const defaultBrandOptions = useMemo(
    () => filterByBrandOptions.filter((brandOption) => brandOption.isDefault),
    [filterByBrandOptions],
  );
  const [brandOptions, setBrandOptions] = useState(defaultBrandOptions);
  const [brandSearchText, setBrandSearchText] = useState<string>('');

  const defaultWhiskyTypeOptions = useMemo(
    () =>
      filterByWhiskyTypeOptions.filter((type) =>
        defaultWhiskyTypes.includes(type.value),
      ),
    [filterByWhiskyTypeOptions],
  );
  const [whiskyTypeOptions, setWhiskyTypeOptions] = useState(
    defaultWhiskyTypeOptions,
  );
  const [whiskyTypeSearchText, setWhiskyTypeSearchText] = useState<string>('');

  const {
    filterParams,
    setFilterParams,
    searchQuery,
    resetSearchQuery,
    hasActiveFilters,
  } = useShopFilterUrlEncode();

  const navigate = useNavigate();

  const handleReset = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // reset brand search
      setBrandSearchText('');
      setBrandOptions(defaultBrandOptions);
      // reset whisky type search
      setWhiskyTypeSearchText('');
      setWhiskyTypeOptions(defaultWhiskyTypeOptions);
      navigate(`${pathname}?${resetSearchQuery}`);
      setIsShopFilterActive(false);
    },
    [
      defaultBrandOptions,
      defaultWhiskyTypeOptions,
      navigate,
      pathname,
      resetSearchQuery,
      setIsShopFilterActive,
    ],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      navigate(`${pathname}?${searchQuery}`);
      setIsShopFilterActive(false);
    },
    [navigate, pathname, searchQuery, setIsShopFilterActive],
  );

  const handleWhiskyTypeFilterTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setWhiskyTypeSearchText(event.target.value);
      const searchText = event.target.value.trim().toLowerCase();
      if (searchText.length > 0) {
        setWhiskyTypeOptions(
          filterByWhiskyTypeOptions.filter(({label}) =>
            label.toLowerCase().includes(searchText),
          ),
        );
      } else {
        setWhiskyTypeOptions(defaultWhiskyTypeOptions);
      }
    },
    [defaultWhiskyTypeOptions, filterByWhiskyTypeOptions],
  );

  const handleBrandFilterTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setBrandSearchText(event.target.value);
      const searchText = event.target.value.trim().toLowerCase();
      if (searchText.length > 0) {
        setBrandOptions(
          filterByBrandOptions.filter(({label}) =>
            label.toLowerCase().includes(searchText),
          ),
        );
      } else {
        setBrandOptions(defaultBrandOptions);
      }
    },
    [defaultBrandOptions, filterByBrandOptions],
  );

  const handleMultiSelectChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const name = event.target.name;
      if (filterParams[name].includes(event.target.value)) {
        setFilterParams({
          ...filterParams,
          [name]: filterParams[name].filter(
            (value) => value !== event.target.value,
          ),
        });
        return;
      }
      setFilterParams({
        ...filterParams,
        [name]: filterParams[name].concat([event.target.value]),
      });
    },
    [filterParams, setFilterParams],
  );

  const handleSliderSlide = useCallback(
    (values: FilterRange, name: string) =>
      setFilterParams({...filterParams, [name]: values}),
    [filterParams, setFilterParams],
  );

  const {searchParams, href} = useUrl();

  const inTwlShopFilterActive = useMemo(() => {
    // inTwlShop can be undefined during first render of /shop page
    if (searchParams.get('inTwlShop') && filterParams.inTwlShop === undefined) {
      return coerceIsTrue(searchParams.get('inTwlShop'));
    }
    return [null, undefined, 'true', true].includes(filterParams.inTwlShop);
  }, [searchParams, filterParams]);

  const navigateWithUpdatedFilterParams = useCallback(
    (updatedParams: ShopFilterParamsType) => {
      navigate(`${pathname}?${getSearchQuery(href, updatedParams)}`);
    },
    [href, navigate, pathname],
  );

  return (
    <div className="shop-filter">
      <form
        onSubmit={handleSubmit}
        onReset={handleReset}
        className={classNames('shop-filter-form', {
          'display-reset': hasActiveFilters,
        })}
      >
        <div className="shop-filter__item mt-20">
          <div className="out-of-stock-option toggle-switch-row">
            <ToggleSwitchField
              name="includeOutOfStock"
              checked={filterParams.inStock === filterInStockOptions.any.value}
              onChange={() => {
                setFilterParams((prev: ShopFilterParamsType) => {
                  const updatedParams = {
                    ...prev,
                    inStock:
                      prev.inStock === filterInStockOptions.any.value
                        ? filterInStockOptions.inStock.value
                        : filterInStockOptions.any.value,
                  };
                  navigateWithUpdatedFilterParams(updatedParams);
                  return updatedParams;
                });
              }}
            >
              Out of stock
            </ToggleSwitchField>
          </div>
        </div>
        <div className="shop-filter__item mt-20">
          <div className="out-of-stock-option toggle-switch-row">
            <ToggleSwitchField
              name="TWL Shop"
              checked={inTwlShopFilterActive}
              onChange={() => {
                setFilterParams((prev: ShopFilterParamsType) => {
                  const updatedParams = {
                    ...prev,
                    inTwlShop: `${!inTwlShopFilterActive}`,
                  };
                  navigateWithUpdatedFilterParams(updatedParams);
                  return updatedParams;
                });
              }}
            >
              TWL Shop
            </ToggleSwitchField>
          </div>
        </div>

        <ShopFilterSelect
          title="By Price"
          name="priceTags"
          options={filterByPriceOptions}
          values={filterParams.priceTags}
          onChange={handleMultiSelectChange}
        />

        {filterByBrandOptions.length > 0 && (
          <ShopFilterSelect
            title="By Brands"
            name="brands"
            options={brandOptions}
            values={filterParams.brands}
            onChange={handleMultiSelectChange}
            hasReadMoreBlock={!brandSearchText}
            hideIfEmpty={false}
          >
            <input
              type="text"
              className={classNames(
                'form__element shop-filter__item__search shop-filter-search-js',
              )}
              name="search-brands"
              placeholder="Search brands"
              autoComplete="off"
              value={brandSearchText}
              onChange={handleBrandFilterTextChange}
            />
            {brandSearchText.length > 0 && brandOptions.length === 0 && (
              <div className="shop-filter-searchable-list-js-feedback">
                No brand matching <strong>&quot;{brandSearchText}&quot;</strong>
              </div>
            )}
          </ShopFilterSelect>
        )}

        <ShopFilterSelect
          title="By Type"
          name="types"
          options={whiskyTypeOptions}
          values={filterParams.types}
          onChange={handleMultiSelectChange}
          hasReadMoreBlock={!whiskyTypeSearchText}
          hideIfEmpty={false}
        >
          <input
            type="text"
            className={classNames(
              'form__element shop-filter__item__search shop-filter-search-js',
            )}
            name="search-types"
            placeholder="Search type"
            autoComplete="off"
            value={whiskyTypeSearchText}
            onChange={handleWhiskyTypeFilterTextChange}
          />
          {whiskyTypeSearchText.length > 0 && whiskyTypeOptions.length === 0 && (
            <div className="shop-filter-searchable-list-js-feedback">
              No type matching{' '}
              <strong>&quot;{whiskyTypeSearchText}&quot;</strong>
            </div>
          )}
        </ShopFilterSelect>

        <ShopFilterSelect
          title="By Country"
          name="countries"
          options={Object.values(CountryOfOrigin).map(
            ({value, label, iconName}) => ({
              value,
              label: (
                <>
                  <VectorIcon iconName={iconName} /> {label}
                </>
              ),
            }),
          )}
          values={filterParams.countries}
          onChange={handleMultiSelectChange}
        />

        <ShopFilterSelect
          title="By Category"
          name="categories"
          options={Object.values(ProductCategory)}
          values={filterParams.categories}
          onChange={handleMultiSelectChange}
        />

        <ShopFilterSlider
          title="By ABV"
          name="abv"
          min={filterRanges.abv.min}
          max={filterRanges.abv.max}
          value={filterParams.abv}
          onSlide={handleSliderSlide}
        >
          <div className="shop-filter__range-values">
            ABV: <strong>{filterParams.abv.min}</strong>% -{' '}
            <strong>{filterParams.abv.max}</strong>%
          </div>
        </ShopFilterSlider>

        <ShopFilterSelect
          title="By User Rating"
          name="ratings"
          options={filterByUserRatingOptions}
          values={filterParams.ratings}
          onChange={handleMultiSelectChange}
        />

        {/* <ShopFilterSelect
          title="By Partner Store"
          name="partnerStores"
          options={filterWhiskyByPartnerStoreOptions}
          values={filterParams.partnerStores}
          onChange={handleMultiSelectChange}
        /> */}

        <ShopFilterSlider
          title="By Age"
          name="age"
          min={filterRanges.age.min}
          max={filterRanges.age.max}
          value={filterParams.age}
          onSlide={handleSliderSlide}
        >
          <div className="shop-filter__range-values">
            AGE:{' '}
            <strong>
              {filterParams.age.min ? filterParams.age.min : 'NAS'}
            </strong>{' '}
            -&nbsp;
            <strong>{filterParams.age.max}</strong>
            &nbsp;years
          </div>
        </ShopFilterSlider>

        <ShopFilterSelect
          title="By Style"
          name="styles"
          options={filterWhiskyByStyleOptions}
          values={filterParams.styles}
          onChange={handleMultiSelectChange}
        />

        <div
          role="button"
          tabIndex={-1}
          className="shop-filter__trigger"
          id="shopFilterTrigger"
          onClick={toggleFilters}
        >
          <div className="shop-filter__trigger-inner">
            <strong>Filter Products</strong>
            <i className="icon-bottom-arrow-circle" />
          </div>
        </div>
        <div className="shop-filter__submit">
          <button
            type="reset"
            className="button button_color_dark button_full shop-filter-reset"
            id="shop-filter-reset"
          >
            Reset Filters
          </button>
          <button
            type="submit"
            className="button button_full"
            id="shop-filter-submit"
          >
            Filter Products
          </button>
        </div>
      </form>
    </div>
  );
}
